// Models
import { ODataVersion } from "../../../service/planner/common/IOdataEntityResource";
import { DiffableClonableODataEntity } from "./DiffableClonableODataEntity";
import isEmpty from "lodash/isEmpty";
// Utilities
import { setEtag } from "../../../utilities/ODataUtilities";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
// #endregion
/**
 * Base class for patchable OData entities.
 *
 * This class provides infrastructure for:
 * - Tracking changes between entity versions
 * - Serializing changes for OData PATCH operations via a mapper function
 * - Handling sub-entity arrays with add/update/delete operations
 *
 * @template T - The entity interface type
 * @template TU - The update resource type
 * @template TMutableKeys - The keys of T that are mutable (defaults to all keys of T)
 */ export class PatchableODataEntity extends DiffableClonableODataEntity {
    /**
     * Creates an update resource containing only the changed properties.
     * @param source The original entity to compare against
     * @param forceIncludeFields Field names whose current value should appear in the resource even
     *        when the diff sees no change. Used by callers that need the API to receive an explicit
     *        value rather than infer it from the rest of the payload (e.g. timeline resize emitting
     *        the finish edge so the server doesn't recompute it from start + duration).
     */ forUpdate(source, forceIncludeFields) {
        if (this.id !== source.id) {
            throw new Error("Cannot update entity with a different ID.");
        }
        const target = this;
        const diff = source.getDiff(target);
        if (forceIncludeFields) {
            // Build a separately-typed bag of forced fields, then merge into diff. The narrow
            // Record<string, unknown> casts stay scoped to the merge so `diff` itself remains a
            // properly-typed DeepPartial<T> for downstream consumers.
            const forced = {};
            const diffRecord = diff;
            const targetRecord = target;
            for (const field of forceIncludeFields){
                const key = field;
                if (diffRecord[key] === undefined) {
                    forced[key] = targetRecord[key];
                }
            }
            Object.assign(diff, forced);
        }
        if (isEmpty(diff)) {
            return null;
        }
        // Mapper returns already-processed resource types
        const changes = this.mapEntityChangesToResource(diff, source);
        // Check if changes object is empty or only contains undefined values
        // JSON.stringify removes undefined values, so we check if the result is an empty object
        if (!changes || JSON.stringify(changes) === "{}") {
            return null;
        }
        return {
            id: this.id,
            version: this.version,
            changes
        };
    }
    // #endregion
    // #region Sub-Entity Processing (Static Helper Methods)
    /**
     * Create identity properties for a sub-entity (@id and @etag)
     * @param id The ID of the sub-entity
     * @param version The version of the sub-entity
     * @param odataVersion The OData version to use
     */ static createSubEntityIdentity(id, version, odataVersion) {
        if (!id || !version) {
            throw new Error("Both id and version must be provided to set patchable sub-entity properties.");
        }
        const result = {
            "@id": id
        };
        setEtag(result, version, odataVersion);
        return result;
    }
    /**
     * Find items that were deleted (exist in source but not in current)
     * @param sourceArray The source array of items
     * @param currentArray The current array of items
     * @param odataVersion The OData version to use
     */ static findDeletedItems(sourceArray, currentArray, odataVersion) {
        if (!Array.isArray(sourceArray) || sourceArray.length === 0) {
            return [];
        }
        const currentIds = new Set(currentArray.map((item)=>item.id));
        return sourceArray.filter((entity)=>!currentIds.has(entity.id)).filter((entity)=>entity.version)// Filter out local temp IDs — they are optimistically created sub-entities that don't exist on the server
        .filter((entity)=>!LocalIdGenerator.isLocalTempId(entity.id)).map((entity)=>({
                "@removed": {},
                ...PatchableODataEntity.createSubEntityIdentity(entity.id, entity.version, odataVersion)
            }));
    }
    /**
     * Process a single sub-entity item (create or update)
     * @param item The item to process
     * @param originalItem The original item to compare against
     * @param odataVersion The OData version to use
     */ static processSubEntityItem(item, originalItem, odataVersion) {
        // Handle undefined/null items
        if (!item) {
            return undefined;
        }
        // Item must be an object
        if (typeof item !== "object") {
            throw new Error("Sub-entity item must be an object.");
        }
        // Create new entities
        if (!originalItem) {
            // If entity has forCreate method, call it
            if ("forCreate" in item && typeof item.forCreate === "function") {
                return item.forCreate();
            }
            // PatchableODataEntity without forCreate - shouldn't happen for new entities
            throw new Error("New entity must have forCreate method");
        }
        // Update existing entities
        if (!item.version) {
            throw new Error("Item must have an '@etag' property for update operations.");
        }
        if (originalItem && item instanceof PatchableODataEntity) {
            const updateResult = item.forUpdate(originalItem);
            if (updateResult?.changes && Object.keys(updateResult.changes).length > 0) {
                return {
                    ...PatchableODataEntity.createSubEntityIdentity(item.id, item.version, odataVersion),
                    ...updateResult.changes
                };
            }
        }
        return undefined;
    }
    /**
     * Process a single sub-entity for update
     * Protected to allow subclasses to call from their mappers
     */ processSubEntity(currentValue, sourceValue) {
        const result = PatchableODataEntity.processSubEntityItem(currentValue, sourceValue, this.odataVersion);
        // Filter out null (which means no changes for update)
        return result ?? undefined;
    }
    /**
     * Process a sub-entity array for update
     * Protected to allow subclasses to call from their mappers
     * Supports both updatable entities (extending PatchableODataEntity) and creatable-only entities
     */ processSubEntityArray(currentArray, sourceArray) {
        // If both source and current are empty, there are no changes
        if ((!Array.isArray(currentArray) || currentArray.length === 0) && (!Array.isArray(sourceArray) || sourceArray.length === 0)) {
            return undefined;
        }
        const results = [];
        // Process deleted items
        const deletedItems = PatchableODataEntity.findDeletedItems(sourceArray || [], currentArray, this.odataVersion);
        results.push(...deletedItems);
        // Process current items (new and updated)
        // Note: We use type assertions here because the union type ICreateableOrUpdatableSubEntity
        // can be either ICreatableSubEntity or IUpdatableSubEntity, and both are compatible
        // with the parameters expected by findOriginalItem and processSubEntityItem
        const processedItems = currentArray.map((item)=>{
            const originalItem = this.findOriginalItemInArray(item, sourceArray || []);
            return PatchableODataEntity.processSubEntityItem(item, originalItem, this.odataVersion);
        }).filter((item)=>item !== undefined && item !== null);
        results.push(...processedItems);
        // Return results if we have changes
        if (results.length > 0) {
            return results;
        }
        // No changes detected
        return undefined;
    }
    /**
     * Helper method to find original item in array for processSubEntityArray
     * This wrapper allows us to work with the union type ICreateableOrUpdatableSubEntity
     */ findOriginalItemInArray(item, sourceArray) {
        if (!Array.isArray(sourceArray) || !item) {
            return undefined;
        }
        return sourceArray.find((srcItem)=>srcItem && srcItem.id === item.id);
    }
    constructor(propertyBag, odataVersion = ODataVersion.Modern){
        super(propertyBag), this.odataVersion = odataVersion;
    }
}
